import { CommonModule, Location } from '@angular/common';
import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, firstValueFrom } from 'rxjs';
import { AppointmentService } from '../services/appointment.service';
import { AuthService } from '../shared/auth/auth.service';
import { StateService } from '../shared/state.service';

@Component({
  selector: 'app-zoom-meeting',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './zoom-meeting.component.html',
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      min-width: 0;
    }

    .zoom-meeting-shell {
      position: relative;
      width: 100%;
      // min-height: calc(70vh - 96px);
      // height: calc(70vh - 96px);
      width: 100%;
      overflow: hidden;
      border-radius: 12px;
      background: #111;
    }

    .zoom-meeting-root {
      width: 100%;
      height: 100%;
      //  min-height: 500px;
    }

    .zoom-meeting-root ::ng-deep .meeting-client {
      width: 100% !important;
      height: 100% !important;
      // min-height: 720px !important;
    }

    .meeting-overlay {
      position: absolute;
      inset: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      padding: 24px;
      text-align: center;
      color: #fff;
      background: #111;
    }

    .meeting-error {
      color: #ffb4ab;
    }
  `]
})
export class ZoomMeetingComponent implements OnInit, OnDestroy {
  @ViewChild('zoomMeetingRoot', { static: true })
  private zoomMeetingRoot?: ElementRef<HTMLElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly appointmentService = inject(AppointmentService);
  private readonly authService = inject(AuthService);
  private readonly stateService = inject(StateService);
  private readonly location = inject(Location);
  private readonly ngZone = inject(NgZone);

  private zoomClient: any = null;
  private connectionChangeHandler?: (payload: any) => void;
  private zoomRootClickHandler?: EventListener;
  private isMeetingClosed = false;
  private loadSequence = 0;

  appointmentId = '';
  patientId = '';
  meetingRole: 0 | 1 = 0;
  loading = true;
  errorMessage = '';

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, queryParams]) => {
      this.appointmentId =
        params.get('appointmentId') ??
        queryParams.get('appointmentId') ??
        queryParams.get('id') ??
        this.getAppointmentIdFromNavigationState() ??
        this.findAppointmentIdFromPatientContext() ??
        '';
      this.patientId =
        params.get('patientId') ??
        queryParams.get('patientId') ??
        this.findParentParam('id');
      this.meetingRole = queryParams.get('role') === '1' ? 1 : 0;

      if (!this.appointmentId) {
        this.loading = false;
        this.errorMessage = 'No appointment was specified for this meeting.';
        return;
      }

      void this.loadAndJoinMeeting();
    });
  }

  ngOnDestroy(): void {
    this.loadSequence += 1;
    this.cleanupZoomClient(true);
  }

  private async loadAndJoinMeeting(): Promise<void> {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.errorMessage = '';

    try {
      const appointment = await firstValueFrom(
        this.appointmentService.getAppointmentForMeeting(this.appointmentId)
      );
      const meetingNumberFromAppointment = this.extractZoomMeetingId(appointment);

      const signatureResponse = await firstValueFrom(
        this.appointmentService.getZoomSignature(this.appointmentId, this.meetingRole)
      );
      if (sequence !== this.loadSequence) return;

      const signature =
        signatureResponse?.signature ??
        signatureResponse?.sdkJWT ??
        signatureResponse?.token ??
        signatureResponse?.data?.signature ??
        '';
      const meetingNumber = String(
        signatureResponse?.meetingNumber ??
        signatureResponse?.meetingId ??
        signatureResponse?.meeting_id ??
        meetingNumberFromAppointment
      ).replace(/\s+/g, '');
      const password = this.extractMeetingPassword(appointment, signatureResponse);
      let zak =
        signatureResponse?.zak ??
        signatureResponse?.zakToken ??
        signatureResponse?.data?.zak ??
        undefined;

      if (this.meetingRole === 1 && !zak) {
        const zakResponse = await firstValueFrom(
          this.appointmentService.getZoomZak(this.appointmentId)
        );
        if (sequence !== this.loadSequence) return;
        zak =
          zakResponse?.zak ??
          zakResponse?.zakToken ??
          zakResponse?.data?.zak ??
          undefined;
      }

      if (!signature) {
        throw new Error('The backend did not return a Zoom Meeting SDK signature.');
      }
      if (!meetingNumber) {
        throw new Error('The appointment does not contain a Zoom meeting number.');
      }
      if (this.meetingRole === 1 && !zak) {
        throw new Error('Starting this meeting as host requires a Zoom ZAK token from the backend.');
      }

      const root = this.zoomMeetingRoot?.nativeElement;
      if (!root) {
        throw new Error('The Zoom meeting container is unavailable.');
      }

      const { default: ZoomMtgEmbedded } = await import('@zoom/meetingsdk/embedded');
      if (sequence !== this.loadSequence) return;

      this.zoomClient = ZoomMtgEmbedded.createClient();
      this.isMeetingClosed = false;
      await this.zoomClient.init({
        zoomAppRoot: root,
        language: 'en-US',
        patchJsMedia: true,
        leaveOnPageUnload: true,
        customize: {
          toolbar: {
            buttons: [
              {
                text: 'Leave meeting',
                onClick: () => this.ngZone.run(() => void this.leaveMeetingFromZoomUi())
              }
            ]
          },
          meetingInfo: ['topic', 'host', 'mn'],
          video: {
            isResizable: true,
            viewSizes: {
              default: {
                width: root.clientWidth || 1280,
                height: root.clientHeight || 720
              },
              ribbon: {
                width: root.clientWidth || 1280,
                height: root.clientHeight || 720
              }
            }
          }
        }
      });
      if (sequence !== this.loadSequence) return;

      this.connectionChangeHandler = (payload: any) => {
        console.warn('Zoom embedded connection changed', payload);
        if (payload?.state === 'Closed') {
          this.ngZone.run(() => this.handleMeetingClosed());
        }
      };
      this.zoomClient.on?.('connection-change', this.connectionChangeHandler);
      this.zoomRootClickHandler = (event: Event) => this.handleZoomRootClick(event);
      root.addEventListener('click', this.zoomRootClickHandler, true);

      const currentUser = this.authService.currentUserValue;
      await this.zoomClient.join({
        signature,
        meetingNumber,
        password,
        userName: currentUser?.display || currentUser?.name || currentUser?.email || 'e-Likita User',
        userEmail: currentUser?.email || undefined,
        zak
      });

      if (sequence === this.loadSequence) {
        this.loading = false;
      }
    } catch (error: any) {
      if (sequence !== this.loadSequence) return;
      console.error('Unable to start embedded Zoom meeting', error);
      this.cleanupZoomClient(false);
      this.loading = false;
      this.errorMessage =
        error?.reason ??
        error?.message ??
        'Unable to start the Zoom meeting inside e-Likita.';
    }
  }

  private handleMeetingClosed(): void {
    if (this.isMeetingClosed) {
      return;
    }

    this.isMeetingClosed = true;
    this.loadSequence += 1;
    this.loading = false;
    this.errorMessage = '';
    this.cleanupZoomClient(false);
    this.location.back();
  }

  private handleZoomRootClick(event: Event): void {
    const label = this.getZoomClickLabel(event);
    if (!/\bleave\b/i.test(label)) {
      return;
    }

    window.setTimeout(() => {
      void this.leaveMeetingFromZoomUi();
    }, 300);
  }

  private async leaveMeetingFromZoomUi(): Promise<void> {
    if (!this.zoomClient || this.isMeetingClosed) {
      return;
    }

    try {
      console.warn('Zoom embedded leave control clicked; calling leaveMeeting fallback.');
      await this.zoomClient.leaveMeeting?.();
    } catch (error) {
      console.warn('Zoom embedded leaveMeeting fallback did not complete.', error);
    }
  }

  private getZoomClickLabel(event: Event): string {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path
      .filter((target): target is HTMLElement => target instanceof HTMLElement)
      .slice(0, 6)
      .map((element) => [
        element.getAttribute('aria-label'),
        element.getAttribute('title'),
        element.textContent
      ].filter(Boolean).join(' '))
      .join(' ')
      .slice(0, 300);
  }

  private cleanupZoomClient(leaveMeeting: boolean): void {
    const client = this.zoomClient;
    if (!client) {
      return;
    }

    if (this.connectionChangeHandler) {
      try {
        client.off?.('connection-change', this.connectionChangeHandler);
      } catch {
        // The SDK may already have removed the listener during shutdown.
      }
      this.connectionChangeHandler = undefined;
    }

    if (this.zoomRootClickHandler) {
      this.zoomMeetingRoot?.nativeElement.removeEventListener('click', this.zoomRootClickHandler, true);
      this.zoomRootClickHandler = undefined;
    }

    this.zoomClient = null;

    if (leaveMeeting) {
      try {
        const leaveResult = client.leaveMeeting?.() ?? client.leave?.();
        if (leaveResult?.catch) {
          leaveResult.catch(() => undefined);
        }
      } catch {
        // The SDK may already have left or may not have completed initialization.
      }
    }

    try {
      client.destroyClient?.();
    } catch {
      // The SDK may already be destroyed.
    }
  }

  private extractZoomMeetingId(appointment: any): string {
    const directId = appointment?.zoomMeetingId ?? appointment?.meetingId ?? appointment?.meeting_id;
    if (directId) return String(directId).trim();

    const extensions = Array.isArray(appointment?.extension) ? appointment.extension : [];
    const meetingIdExtension = extensions.find((extension: any) =>
      String(extension?.url ?? '').toLowerCase().includes('zoom-meeting-id')
    );
    const extensionId =
      meetingIdExtension?.valueString ??
      meetingIdExtension?.valueInteger ??
      meetingIdExtension?.valueId;
    if (extensionId) return String(extensionId).trim();

    const meetingUrl = this.getMeetingLink(appointment);
    return meetingUrl.match(/\/j\/(\d+)/i)?.[1] ?? '';
  }

  private extractMeetingPassword(appointment: any, signatureResponse: any): string {
    const directPassword =
      signatureResponse?.password ??
      signatureResponse?.passcode ??
      signatureResponse?.meetingPassword ??
      appointment?.zoomPassword ??
      appointment?.meetingPassword ??
      appointment?.password ??
      appointment?.passcode ??
      this.extractZoomPasswordExtension(appointment);
    if (directPassword !== undefined && directPassword !== null) {
      return String(directPassword);
    }

    const meetingUrl = this.getMeetingLink(appointment);
    try {
      return meetingUrl ? new URL(meetingUrl).searchParams.get('pwd') ?? '' : '';
    } catch {
      return '';
    }
  }

  private getMeetingLink(appointment: any): string {
    const direct = appointment?.zoomMeetingUrl || appointment?.meetingLink || appointment?.meetingUrl;
    if (direct) return String(direct).trim();

    const extensions = Array.isArray(appointment?.extension) ? appointment.extension : [];
    for (const extension of extensions) {
      const candidate = extension?.valueUrl || extension?.valueUri || extension?.valueString;
      if (candidate && /^https?:\/\//i.test(String(candidate).trim())) {
        return String(candidate).trim();
      }
    }

    const textCandidates = [
      appointment?.comment,
      appointment?.patientInstruction,
      appointment?.description
    ];
    for (const text of textCandidates) {
      const match = typeof text === 'string' ? text.match(/https?:\/\/[^\s]+/i) : null;
      if (match?.[0]) return match[0];
    }
    return '';
  }

  private extractZoomPasswordExtension(appointment: any): string {
    const extensions = Array.isArray(appointment?.extension) ? appointment.extension : [];
    const passwordExtension = extensions.find((extension: any) =>
      String(extension?.url ?? '').toLowerCase().includes('zoom-meeting-password')
    );
    return String(
      passwordExtension?.valueString ??
      passwordExtension?.valueCode ??
      ''
    ).trim();
  }

  private findParentParam(name: string): string {
    let current = this.route.parent;
    while (current) {
      const value = current.snapshot.paramMap.get(name);
      if (value) return value;
      current = current.parent;
    }
    return '';
  }

  private getAppointmentIdFromNavigationState(): string {
    const state = history.state ?? {};
    const appointmentId =
      state.appointmentId ??
      state.appointment?.id ??
      state.appointment?.appointmentId ??
      '';
    return String(appointmentId || '').trim();
  }

  private findAppointmentIdFromPatientContext(): string {
    const patientId = this.patientId || this.findParentParam('id') || this.stateService.currentPatientId$.getValue();
    const appointments = this.stateService.PatientResources.appointments.getValue() ?? [];
    const candidates = appointments
      .map((entry: any) => entry?.actualResource ?? entry)
      .filter((appointment: any) => !!appointment?.id)
      .filter((appointment: any) => this.isAppointmentForPatient(appointment, patientId))
      .filter((appointment: any) => !!this.extractZoomMeetingId(appointment) || !!this.getMeetingLink(appointment))
      .sort((a: any, b: any) => this.getAppointmentTime(b) - this.getAppointmentTime(a));

    return String(candidates[0]?.id ?? '').trim();
  }

  private isAppointmentForPatient(appointment: any, patientId: string | null): boolean {
    if (!patientId) return true;
    const patientReference = `Patient/${patientId}`;
    return (appointment?.participant ?? []).some((participant: any) =>
      participant?.actor?.reference === patientReference
    );
  }

  private getAppointmentTime(appointment: any): number {
    const value = appointment?.start ?? appointment?.created ?? appointment?.meta?.lastUpdated;
    const time = value ? new Date(value).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  }
}
